import { NgClass } from '@angular/common'
import { AfterViewInit, Component, ElementRef, inject, isDevMode, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatIconModule } from '@angular/material/icon'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import { NewVersionModalComponent } from '@auth/components/new-version-modal/new-version-modal.component'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment.prod'
import { NbCardModule, NbDialogRef, NbDialogService, NbIconLibraries, NbLayoutModule } from '@nebular/theme'
import { EMPTY, interval, noop, Subject } from 'rxjs'
import { catchError, filter, startWith, takeUntil } from 'rxjs/operators'
import { EventDatesTimingsConvertHook } from './modules/events/models/event-dates-timings.convert.hook'
import { EventParser } from './modules/events/models/event.model'
import { IsVirtualConvertHook } from './modules/events/models/is-virtual.convert.hook'
import { LinksConvertHook } from './modules/events/models/links.convert.hook'
import { BetaFeaturesService } from './services/beta-features.service'
import { PosthogService } from './services/posthog.service'
import { ToastrService } from './services/toastr.service'
import { V2TranslationService } from './v2/services/translation.service'
import { NavbarComponent } from './v2/shared/components/navbar/navbar.component'
import { Alert } from './v2/shared/enums/alert.enum'
import { AlertService } from './v2/shared/services/alert.service'
import { AppService } from './v2/shared/services/app.service'
import { ThemeService } from './v2/shared/services/theme.service'

declare let gtag

/**
 * Main application bootstrap component
 */
@Component({
  selector: 'ngx-app',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  imports: [NgClass, NbLayoutModule, NavbarComponent, MatIconModule, NbCardModule, RouterModule]
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  private translationService = inject(V2TranslationService)
  private _appService = inject(AppService)
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /**
   * Whether we should display the sidebar
   */
  showSidebar = false
  /**
   * Displayed popup modal status (For privacy policy and terms of services)
   */
  popStatus
  /**
   * Application navigation menu
   */
  menu = []
  /**
   * Current navigated route
   */
  currentUrl: string
  /**
   * public mode is used for venue capacity dashboards display that allows the authenticated users to change it
   */
  publicMode = true
  /**
   * admin mode is used to disable layout scrolling for admin tools to prevent double scrollbars
   */
  adminMode = false
  /**
   * Displayed modal dialog reference
   */
  @ViewChild('dialog') dialog: TemplateRef<any>
  @ViewChild('navbar') navbar: ElementRef<HTMLElement>
  /**
   * Whether the user is logged in or not
   */
  get loggedIn() {
    return !!this.authService.user && !!this.authService.currentWorkspace
  }

  constructor(
    private router: Router,
    private dialogService: NbDialogService,
    private iconLibraries: NbIconLibraries,
    public authService: AuthService,
    private toastr: ToastrService,
    private themeService: ThemeService,
    private _posthogService: PosthogService,
    private _betaFeaturesService: BetaFeaturesService,
    private _alertService: AlertService
  ) {
    // window.location.href = 'https://micetribe.com/under-maintenance/'
    if (environment.gaMeasurementId) {
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + environment.gaMeasurementId
      document.head.prepend(script)
    }
    console.group('Initializing global event parsing convert hooks')
    /**
     * Register conversion hooks
     */
    EventParser.registerGlobalConvertHook(new LinksConvertHook())
    EventParser.registerGlobalConvertHook(new IsVirtualConvertHook())
    EventParser.registerGlobalConvertHook(new EventDatesTimingsConvertHook())
    console.info('Initialized global event parsing convert hooks ' + EventParser.globalConvertHooks)
    console.groupEnd()
  }
  /**
   * OnInit implementation also setups the icons libraries
   */

  ngAfterViewInit() {
    this.translationService.setLanguage(this.translationService.currentLang())
  }

  ngOnInit() {
    // Theme is automatically initialized in ThemeService constructor
    // Expose theme service methods to window for debugging
    if (isDevMode()) {
      // Expose for debugging only in dev mode
      ;(window as any).themeService = this.themeService
    }
    this.authService.displayPrivacy$
      .pipe(
        filter((result) => result),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: () => {
          this.open(this.dialog, 'dpr')
        }
      })

    this.authService.displayTerms$
      .pipe(
        filter((result) => result),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: () => {
          this.open(this.dialog, 'tc')
        }
      })
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroy$)
      )
      .subscribe({
        next: (event: NavigationEnd) => {
          try {
            if (environment.gaMeasurementId) gtag('config', environment.gaMeasurementId, { page_path: event.urlAfterRedirects })
          } catch (error) {
            console.error(error.message)
          }
          this.publicMode = event.url.startsWith('/capacity') || event.url.endsWith('/venue-capacity')
        }
      })
    this.iconLibraries.registerFontPack('font-awesome', {
      packClass: 'fas',
      iconClassPrefix: 'fa'
    })
    this.iconLibraries.registerFontPack('brands', {
      packClass: 'fab',
      iconClassPrefix: 'fa'
    })
    this.iconLibraries.registerSvgPack('exports', {
      excel: '<img src="https://res.cloudinary.com/contactless/image/upload/v1613303999/planning-system/xlsx.svg" height="15px" width="15px">',
      benchmark: '<img src="https://res.cloudinary.com/contactless/image/upload/v1613304299/planning-system/benchmark.svg"  height="15px" width="15px">',
      database: '<img src="https://res.cloudinary.com/contactless/image/upload/v1649119987/planning-system/upload_file_white_24dp.svg"  height="20px" width="20px">'
    })
    this.checkVersion()

    this.iconLibraries.setDefaultPack('font-awesome')
    this.authService.userChange.pipe(takeUntil(this._destroy$)).subscribe((next) => {
      if (!next) return

      // Load beta features when user logs in
      if (this.authService.isLoggedIn)
        this._betaFeaturesService
          .reloadFeatures()
          .pipe(takeUntil(this._destroy$))
          .subscribe({
            next: () => noop,
            error: (error) => {
              this._alertService.alert(Alert.ERROR, error.error?.message || 'Failed to load feature flags')
            }
          })

      if (next?.is_admin) {
        this.showSidebar = true
      } else {
        if (!!this.authService.currentWorkspace) {
          this.showSidebar = true
        } else {
          this.showSidebar = false
        }
      }
    })
    this.authService.currentWorkspaceChange.pipe(takeUntil(this._destroy$)).subscribe((next) => {
      const billingUrl = this.authService.getOrganizationBillingUrl() ?? '/workspaces/list'
      this.menu = [
        {
          title: 'Workspaces',
          icon: 'briefcase',
          link: '/workspaces/list'
        },
        {
          title: 'Billing',
          icon: 'credit-card',
          link: billingUrl
        },
        {
          title: 'Settings',
          icon: 'settings',
          link: '/app-settings'
        }
      ]
    })
    this._detectUserLocation()
    this._setPosthogUserIfExists()
  }

  /**
   * Sets the posthog user if exists
   */
  private _setPosthogUserIfExists() {
    if (!this.isLoggedIn) return
    this._posthogService.setUser(this.authService.user)
  }
  /**
   * Detects the user location
   */
  private _detectUserLocation() {
    this._appService.getDetectedLocationAuto().pipe(takeUntil(this._destroy$)).subscribe()
  }

  /**
   * New version modal reference
   */
  private _modalRef: NbDialogRef<NewVersionModalComponent>
  /**
   * Checks the application version on development, staging, live environment
   * every 60 seconds on staging and development
   * increased to 600 seconds (10 minutes) on live environments
   * @returns void
   */
  checkVersion() {
    if (['localhost', '127.0.0.1', 'plan.local.micetribe.com'].includes(location.hostname)) {
      return
    }

    const versionCheckSubscription = interval(60000)
      .pipe(
        startWith(0),
        filter(() => !this._modalRef?.componentRef?.instance),
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this.authService
          .getLiveVersion()
          .pipe(
            catchError(() => {
              versionCheckSubscription.unsubscribe()
              return EMPTY
            }),
            takeUntil(this._destroy$)
          )
          .subscribe((data) => {
            if (data.version !== environment.appVersion) {
              this.authService._version = data.version
              this._modalRef = this.dialogService.open(NewVersionModalComponent)
            }
          })
      })
  }

  /**
   * Opens a ToS or PP dialog
   * @param dialog Dialog to open or template reference
   * @param status Dialog status - used for tos and pp
   */
  open(dialog: TemplateRef<any>, status: string) {
    this.popStatus = status
    this.dialogService.open(dialog)
  }

  /**
   * Resend an unvalidated user confirmation email
   */
  resendConfirmation() {
    this.authService
      .confirmRegisteredEmail(this.authService.user['email'])
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (data) => {
          this.toastr.success('Email confirmation sent ', '')
        },
        (error) => {
          this.toastr.error('Failed to send email confirmation', '')
        }
      )
  }

  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /**
   * tests whether the current route is a venue capacity dashboard route
   */
  get largePaddingTop() {
    return (
      this.authService.user !== null &&
      !this.authService.user['confirmed'] &&
      !(/^\/events\/.*\/venue-capacity/.test(this.router.url) || /^\/capacity\/.*\/.*\/.*\/.*/.test(this.router.url))
    )
  }

  /**
   * tests whether the current route is a venue capacity dashboard route
   */
  get wizardTop() {
    return this.navbar?.nativeElement?.clientHeight ?? 0
  }

  /**
   * gets whether the user is logged in or not
   */
  get isLoggedIn(): boolean {
    return this.authService.isLoggedIn && !this.router.url.includes('auth')
  }

  /**
   * gets the current router URL
   */
  get currentRouterUrl(): string {
    return this.router.url
  }
}
